import { TeoPDF } from '@/utils/teo-pdf/TeoPDF';

import type { ConfigurationSetup } from '../../config/config-setup.types';
import type { AnalysisStatistics, SubEvaluation } from '../../TeoApi';
import type { AnalysisConfiguration } from '../../text/TeoText';
import { renderAnalyseKonfiguration } from './analyse-konfiguration';
import { createPageElementsRenderer, FOOTER_HEIGHT } from './page-elements';
import { renderQualitativeAnalyse } from './qualitative-analyse';
import { renderStatistics } from './statistics';
import { renderTextPerformanceIndex } from './text-performance-index';

export interface ReportData {
  reportId: string;
  createdAt: number;
  title?: string;
  indices: Maybe<Record<string, SubEvaluation>>;
  configuration: Maybe<Partial<AnalysisConfiguration>>;
  configSetup: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Maybe<ConfigurationSetup<any>>
    | Error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | Promise<ConfigurationSetup<any>>
    | Promise<Error>;
  statistics: AnalysisStatistics;
  text: string;
  tpi?: Maybe<number>;
  tpiTarget: number;
}

export async function renderPDFReport({
  indices: analysisData,
  configuration,
  configSetup,
  statistics,
  text,
  tpi,
  tpiTarget,
  title = 'TEO Report',
  createdAt,
  reportId,
}: ReportData) {
  const resolvedConfigSetup = await configSetup;
  const pdf = new TeoPDF({
    size: 'A4',
    margins: {
      left: 32, // 1cm = 28.3 // 1em = 12
      right: 32,
      top: 32,
      bottom: FOOTER_HEIGHT + 16,
    },
    autoFirstPage: false,
    bufferPages: true,
  });
  pdf.doc.info.CreationDate = new Date();
  pdf.doc.info.Title = title + ' ' + reportId;
  pdf.doc.info.Producer = 'TCL.digital TEO V3';
  const pes = createPageElementsRenderer({
    reportId,
    createdAt,
    title,
  });
  pdf.doc.on('pageAdded', () => {
    pes.renderHeader(pdf);
  });

  pdf.doc.addPage();
  renderAnalyseKonfiguration(pdf, configuration, resolvedConfigSetup);
  renderTextPerformanceIndex(pdf, tpiTarget, tpi);
  renderQualitativeAnalyse(pdf, analysisData);

  pdf.addPage().setFontColor('black');
  renderStatistics(pdf, {
    statistics,
    extraEvaluations: {
      // beutreilung hängt nicht vom Wert ab, sondern vom activation score
      impuls_weight_per_sentence: analysisData?.activation?.score,
    },
    indexUsed: Object.entries(analysisData ?? {})
      .filter(([, { used }]) => !!used)
      .map(([key]) => key),
  });

  pdf.addPage().setFontColor('black');
  pdf.addText(text);

  // -- add footers
  const { count, start } = pdf.doc.bufferedPageRange();
  for (let i = start; i < count; i++) {
    pdf.doc.switchToPage(i);
    pes.renderFooter(pdf);
  }
  return pdf;
}
